import React, { useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Link,
  Chip as MuiChip,
  IconButton,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import styled from "@emotion/styled";
import PopoverTarga from "../../components/PopoverTarga";
import PopoverUser from "../../components/PopoverUser";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Person, AccountBalance } from "@mui/icons-material";
import GridOnIcon from "@mui/icons-material/GridOn";
import GavelIcon from "@mui/icons-material/Gavel";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useSelector } from "react-redux";
import PopoverReboot from "../../components/PopoverReboot";
import ExcelIcon from "./ExcelIcon";
import { downloadDocument } from "./useDownloadDocument";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/slices/snackbar";

const Chip = styled(MuiChip)`
  height: 20px;
  padding: 4px 0;
  font-size: 90%;
  background-color: ${(props) =>
    props.theme.palette[props.color ? props.color : "primary"]};
  color: ${(props) => props.theme.palette.common.white};
`;
// Group Flussi Columns
export const useGetFlussiTableColumns = (setIdFlusso) => {
  const { flotteDownload } = useAuth();
  const dispatch = useDispatch();
  const [loadingId, setLoadingId] = useState(null);
  const navigate = useNavigate();
  const handleSnackbar = (message, severity) => {
    dispatch(showSnackbar({ message: message, severity: severity }));
  };

  const groupColumns = [
    {
      field: "flusso",
      headerName: "Flusso",
      sortable: false,
      filterable: false,
      minWidth: 150,
      flex: 1,
      renderCell: (params) => {
        const { flusso } = params.row;

        const handleSelectFlusso = () => {
          setIdFlusso({ flusso: params.row.flusso, stato: params.row.stato });
        };

        const handleDownloadDocument = () => {
          console.log("Mock download for Flusso:", flusso);
          handleSnackbar("Mock: Documento scaricato con successo!", "success");
        };

        return (
          <Box
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            <ExcelIcon
              handleExcelIconClick={handleDownloadDocument}
              disabled={false}
            />
            {/* Open in Analyzer logic - keeping but using flusso as id */}
            <IconButton
              size="small"
              sx={{ marginLeft: 1 }}
              /* onClick={() => {
                navigate(`/pages/flottaDetails?fileId=${flusso}`);
              }} */
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
            <Box
              onClick={handleSelectFlusso}
              sx={{
                cursor: "pointer",
                minWidth: "100px",
                color: "#0062ff",
                marginLeft: 1,
                fontWeight: "bold",
              }}
            >
              {flusso}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "stato",
      headerName: "Stato",
      sortable: false,
      filterable: false,
      minWidth: 150,
      flex: 1,
      renderCell: (params) => {
        const { stato, targheTotali } = params.row;

        const completed = stato === "Completato";
        const loading = stato === "In Corso" || stato === "In Lavorazione";

        const ChipItem = ({ color, showLoading, clickable }) => (
          <Chip
            label={
              <Box sx={{ display: "flex", flexDirection: "row" }}>
                {stato}
                {showLoading && (
                  <CircularProgress
                    size={14}
                    sx={{ marginLeft: "8px", color: "white", marginTop: "1px" }}
                  />
                )}
              </Box>
            }
            color={color}
            sx={clickable ? { cursor: "pointer" } : {}}
          />
        );

        const getChip = () => {
          if (completed) return <ChipItem color="success" />;
          if (loading) return <ChipItem color="warning" showLoading />;
          return <ChipItem color="error" />;
        };

        return (
          <>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {getChip()}
            </Box>
            <Box sx={{ fontWeight: "600", ml: 4 }}>
              {`${targheTotali} Targhe`}
            </Box>
          </>
        );
      },
    },
  ];

  return groupColumns;
};

// Flussi Details Columns
export const useGetFlussiDetailsTableColumns = () => {
  const [userData, setUserData] = useState(null);
  const [targaData, setTargaData] = useState(null);
  const [canViewUserData, setCanViewUserData] = useState(false);
  const [canViewTargaData, setCanViewTargaData] = useState(false);
  const [isTargaPopupOpen, setIsTargaPopupOpen] = useState(false);
  const [isRebootPopupOpen, setIsRebootPopupOpen] = useState(false);
  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [currentTargaRef, setCurrentTargaRef] = useState(null);
  const [currentUserRef, setCurrentUserRef] = useState(null);
  const [currentRebootRef, setCurrentRebootRef] = useState(null);
  const role = useSelector((state) => state.role.role);
  const isAdm = ["supAdm", "adm"].includes(role);
  const rebootRefs = useRef({});
  const targaRefs = useRef({});
  const userRefs = useRef({});
  const navigate = useNavigate();
  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const handleCloseTargaPopup = () => {
    setIsTargaPopupOpen(false);
    setCurrentTargaRef(null);
  };

  const handleCloseRebootPopup = () => {
    setIsRebootPopupOpen(false);
    setCurrentRebootRef(null);
  };

  const handleCloseUserPopup = () => {
    setIsUserPopupOpen(false);
    setCurrentUserRef(null);
  };

  const handleOpenTargaPopup = (ref, targaId) => {
    console.log("Mock getPreviewInfo for target:", targaId);
    setTargaData(null);
    setCanViewTargaData(false);
    setIsTargaPopupOpen(true);
    setCurrentTargaRef(ref);
  };

  const handleOpenUserPopup = (ref, utenteIDUID) => {
    console.log("Mock getPreviewInfo for user:", utenteIDUID);
    setUserData(null);
    setCanViewUserData(false);
    setIsUserPopupOpen(true);
    setCurrentUserRef(ref);
  };

  const fleetColumns = [
    {
      field: "cliente",
      headerName: "Cliente",
      sortable: false,
      filterable: false,
      minWidth: 250,
      flex: 1.5,
      renderCell: (params) => {
        const cfPiva = params.row["CODICE FISCALE / PIVA ASSICURATO"];
        const cognome = params.row["COGNOME / RAGIONE SOCIALE ASSICURATO"];
        const nome = params.row["NOME ASSICURATO "];
        return (
          <Box sx={{ display: "flex", flexDirection: "column", py: 2 }}>
            <Box sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>{cfPiva}</Box>
            <Box sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              {`${cognome || ""} ${nome || ""}`}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "veicolo",
      headerName: "Veicolo",
      sortable: false,
      filterable: false,
      minWidth: 200,
      flex: 1,
      renderCell: (params) => {
        const targa = params.row["TARGA VEICOLO"];
        const telaio = params.row["TELAIO VEICOLO"];
        const marca = params.row["MARCA VEICOLO"];
        const modello = params.row["MODELLO VEICOLO "];
        return (
          <Box sx={{ display: "flex", flexDirection: "column", py: 2 }}>
            <Box
              sx={{
                fontWeight: "bold",
                color: "primary.main",
                fontSize: "0.9rem",
              }}
            >
              {targa}
            </Box>
            <Box sx={{ fontSize: "0.75rem", fontFamily: "monospace" }}>
              {telaio}
            </Box>
            <Box sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              {`${marca || ""} ${modello || ""}`}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "indirizzo",
      headerName: "Indirizzo",
      sortable: false,
      filterable: false,
      minWidth: 250,
      flex: 1.2,
      renderCell: (params) => {
        const indirizzo = params.row["INDIRIZZO RESIDENZA ASSICURATO"];
        const cap = params.row["CAP RESIDENZA ASSICURATO "];
        const citta = params.row["CITTA’ RESIDENZA ASSICURATO"];
        const provincia = params.row["PROVINCIA RESIDENZA ASSICURATO"];
        return (
          <Box sx={{ display: "flex", flexDirection: "column", py: 2 }}>
            <Box sx={{ fontSize: "0.85rem" }}>{indirizzo}</Box>
            <Box sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              {`${cap || ""} ${citta || ""} (${provincia || ""})`}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "datiVeicolo",
      headerName: "Dati Veicolo",
      sortable: false,
      filterable: false,
      minWidth: 200,
      flex: 1,
      renderCell: (params) => {
        const tipologia = params.row["TIPOLOGIA VEICOLO"];
        const pesoVeicolo = params.row["PESO VEICOLO "];
        const dataImmatricolazione = params.row["DATA IMMATRICOLAZIONE"];
        return (
          <Box sx={{ display: "flex", flexDirection: "column", py: 2 }}>
            <Box sx={{ fontSize: "0.85rem", fontWeight: 500 }}>
              Tipologia: {tipologia}
            </Box>
            <Box sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              {`Peso: ${pesoVeicolo || "-"}`}
            </Box>
            <Box sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              {`Imm: ${dataImmatricolazione || "-"}`}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "copertura",
      headerName: "Copertura",
      sortable: false,
      filterable: false,
      minWidth: 200,
      flex: 1,
      renderCell: (params) => {
        const dataInizioCopertura =
          params.row["DATA INIZIO VALIDITA' COPERTURA"];
        const dataFineCopertura = params.row["DATA FINE VALIDITA' COPERTURA"];
        return (
          <Box sx={{ display: "flex", flexDirection: "column", py: 2 }}>
            <Box sx={{ fontSize: "0.85rem", display: "flex", gap: 1 }}>
              <Box component="span" sx={{ color: "success.main" }}>
                Da:
              </Box>{" "}
              {dataInizioCopertura}
            </Box>
            <Box sx={{ fontSize: "0.85rem", display: "flex", gap: 1 }}>
              <Box component="span" sx={{ color: "error.main" }}>
                A:
              </Box>{" "}
              {dataFineCopertura}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "NUMERO POLIZZA",
      headerName: "N. Polizza",
      width: 150,
    },
    {
      field: "LOB",
      headerName: "LOB",
      width: 80,
    },
    {
      field: "TIPOLOGIA POLIZZA",
      headerName: "Tipo Polizza",
      width: 120,
    },
    {
      field: "CODICE CONFIGURAZIONE",
      headerName: "Cod. Config.",
      width: 120,
    },
    {
      field: "IDENTIFICATIVO UNIVOCO APPLICAZIONE ",
      headerName: "ID Univoco App",
      renderCell: (params) => {
        const identificativoUnivocoApplicazione =
          params.row["IDENTIFICATIVO UNIVOCO APPLICAZIONE "];
        return (
          <Box sx={{ display: "flex", flexDirection: "column", py: 2 }}>
            {identificativoUnivocoApplicazione}
          </Box>
        );
      },
      width: 150,
    },
    {
      field: "TIPO OGGETTO ASSICURATO",
      headerName: "Tipo Ogg. Assic.",
      width: 130,
    },
    {
      field: "DATA RENDICONTAZIONE",
      headerName: "Data Rendicontazione",
      width: 175,
    },
    {
      field: "CODICE DOMANDA 1",
      headerName: "Codice D1",
      width: 200,
    },
    {
      field: "CODICE RISPOSTA 1",
      headerName: "Codice R1",
      align: "center",
      width: 125,
    },
    {
      field: "CODICE DOMANDA 2",
      headerName: "Codice D2",
      width: 200,
    },
    {
      field: "CODICE RISPOSTA 2",
      headerName: "Codice R2",
      width: 125,
    },
    {
      field: "CODICE DOMANDA 3",
      headerName: "Codice D3",
      width: 200,
    },
    {
      field: "CODICE RISPOSTA 3",
      headerName: "Codice R3",
      width: 125,
    },
    {
      field: "CODICE DOMANDA 4",
      headerName: "Codice D4",
      width: 200,
    },
    {
      field: "CODICE RISPOSTA 4",
      headerName: "Codice R4",
      width: 125,
    },
    {
      field: "CODICE DOMANDA 5",
      headerName: "Codice D5",
      width: 200,
    },
    {
      field: "CODICE RISPOSTA 5",
      headerName: "Codice R5",
      width: 125,
    },
    {
      field: "TIPO MOVIMENTO",
      headerName: "Tipo Movim.",
      width: 100,
    },
  ];

  return fleetColumns;
};
